/**
 * Single source of truth cho 8 báo cáo con: mã, path, nhãn, mô tả ngắn, icon, visibility filter.
 * Dùng chung bởi ReportShell, ReportBreadcrumb, ReportTabsNav và các page.
 */
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CloudRain,
  Droplets,
  FileWarning,
  History,
  MapPinned,
  Radio,
  type LucideIcon,
} from 'lucide-react';

import { PATHS } from '@/config/paths';

import type { ReportFilterVisibility } from '../components/ReportFilterPanel';
import type { ReportCode } from '../types/report.types';

export interface ReportMeta {
  code: ReportCode;
  path: string;
  /** Tên dài hiển thị trên header & breadcrumb. */
  title: string;
  /** Tên ngắn cho tab/menu. */
  shortTitle: string;
  /** Mô tả 1 câu cho header. */
  description: string;
  icon: LucideIcon;
  /** Filter nào hiển thị trên sidebar. Không khai = mặc định ReportFilterPanel. */
  filterVisibility?: ReportFilterVisibility;
}

export const REPORT_META: readonly ReportMeta[] = [
  {
    code: 'ops.reports.overview',
    path: PATHS.OPS_REPORTS_OVERVIEW,
    title: 'Tổng quan',
    shortTitle: 'Tổng quan',
    description: 'Theo dõi nhanh tình hình: ngập, cảnh báo, trạm đo và khu vực cần lưu ý.',
    icon: Activity,
    filterVisibility: { metric: false, severity: true },
  },
  {
    code: 'ops.reports.stations',
    path: PATHS.OPS_REPORTS_STATIONS,
    title: 'Trạm giám sát',
    shortTitle: 'Trạm giám sát',
    description: 'Tình trạng hoạt động, tín hiệu, nguồn và đủ dữ liệu theo từng trạm.',
    icon: Radio,
    filterVisibility: { severity: false, metric: false, dataMode: false },
  },
  {
    code: 'ops.reports.water_level',
    path: PATHS.OPS_REPORTS_WATER_LEVEL,
    title: 'Mực nước',
    shortTitle: 'Mực nước',
    description: 'Mức nước theo trạm và lưu vực, có so với ngưỡng cảnh báo.',
    icon: Droplets,
    filterVisibility: { severity: false, metric: false },
  },
  {
    code: 'ops.reports.rainfall',
    path: PATHS.OPS_REPORTS_RAINFALL,
    title: 'Lượng mưa',
    shortTitle: 'Lượng mưa',
    description: 'Lượng mưa tích lũy, cường độ và vùng chịu ảnh hưởng.',
    icon: CloudRain,
    filterVisibility: { severity: false, metric: false },
  },
  {
    code: 'ops.reports.alerts',
    path: PATHS.OPS_REPORTS_ALERTS,
    title: 'Cảnh báo',
    shortTitle: 'Cảnh báo',
    description: 'Cảnh báo theo mức độ, thời gian xử lý và trạm phát sinh nhiều nhất.',
    icon: AlertTriangle,
    filterVisibility: { severity: true, metric: false },
  },
  {
    code: 'ops.reports.flood_events',
    path: PATHS.OPS_REPORTS_FLOOD_EVENTS,
    title: 'Sự kiện ngập',
    shortTitle: 'Sự kiện ngập',
    description: 'Danh sách đợt ngập, độ sâu, diện tích, phân bố theo khu vực.',
    icon: MapPinned,
    filterVisibility: { stations: false, severity: true, metric: false },
  },
  {
    code: 'ops.reports.damage',
    path: PATHS.OPS_REPORTS_DAMAGE,
    title: 'Thiệt hại',
    shortTitle: 'Thiệt hại',
    description: 'Tổng hợp thiệt hại tài sản, hạ tầng và người theo từng đợt ngập.',
    icon: FileWarning,
    filterVisibility: { stations: false, severity: false, metric: false, dataMode: false },
  },
  {
    code: 'ops.reports.historical_comparison',
    path: PATHS.OPS_REPORTS_HISTORICAL,
    title: 'So sánh theo năm',
    shortTitle: 'So sánh theo năm',
    description: 'So sánh số liệu ngập, mưa, cảnh báo và thiệt hại qua các năm.',
    icon: History,
    filterVisibility: {
      granularity: false,
      provinces: false,
      wards: false,
      stations: false,
      severity: false,
      metric: false,
      dataMode: false,
    },
  },
];

/** Tra cứu nhanh theo `code`. */
export const REPORT_META_BY_CODE: ReadonlyMap<ReportCode, ReportMeta> = new Map(
  REPORT_META.map((m) => [m.code, m]),
);

/** Chỉ dùng cho router / active-tab detection. */
export function findReportMetaByPath(pathname: string): ReportMeta | undefined {
  return REPORT_META.find((m) => pathname.startsWith(m.path));
}

/** Lucide-icon chung cho landing báo cáo. */
export { BarChart3 as DefaultReportIcon };
